import React, { useState, useContext, useMemo } from 'react'
import dynamic from 'next/dynamic'
import {
  Grid,
  Select,
  Typography,
  InputLabel,
  Button,
  MenuItem,
  LocationOnIcon,
  Hidden,
  ArrowRightAlt,
  styledComponent,
  useTheme,
  Checkbox,
  FormControlLabel,
  Theme
} from '@hermes/web-components'
import { SearchProfilesPageContext } from '../../../../providers/SearchProfilesPage'
// eslint-disable-next-line import/no-cycle
import MobileFilterDetailsModal from '../../../search/MobileFilterDetailsModal'
import useTranslateMessage from '../../../../hooks/useTranslateMessage'
import { Insurer, Language, ProfileKeywordRelated } from '../../../../types'
import useSearchFiltersConfig, {
  ButtonFilterType,
  CheckboxFilterType,
  DropdownFilterType,
  FilterType
} from '../../../search/hooks/useSearchFiltersConfig'

const StyledCheckbox = styledComponent(Checkbox)(({ theme }: { theme: Theme }) => ({
  '& .MuiSvgIcon-root': {
    fontSize: '24px !important'
  },
  ...(theme?.custom?.SearchFilters?.checkbox ?? {})
}))

const StyledSelect = styledComponent(Select)(({ theme }) => ({
  '& .MuiList-root': {
    maxWidth: '240px',
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    background: '#FFFFFF',
    border: '1px solid #E7EAEF',
    boxShadow: '0px 6px 10px rgba(2, 3, 3, 0.03), 0px 1px 18px rgba(2, 3, 3, 0.02), 0px 3px 5px rgba(2, 3, 3, 0.04)',
    '&::-webkit-scrollbar': {
      width: 8,
      borderRadius: 8,
      background: '#D5DEF9'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.custom.scrollbarThumb.backgroundColor,
      borderRadius: 8
    }
  },
  ...(theme?.custom?.SearchFilters?.select ?? {})
}))

const StyledMenuItem = styledComponent(MenuItem)(({ theme }: { theme: Theme }) => ({
  '& .MuiMenuItem-root': {
    borderWidth: 0,
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13.33px',
    fontFamily: theme.typography.fontFamily,
    flexDirection: 'row',
    alignItems: 'center',
    height: '36px !important',
    borderRadius: 0,
    flex: 'none',
    order: 0,
    alignSelf: 'stretch',
    flexGrow: 0
  },
  '&:hover': theme?.custom?.SearchFilters?.menuItemHover ?? {}
}))

const StyledFilterButton = styledComponent(Button)(({ theme }: { theme: Theme }) => ({
  ...(theme?.custom?.SearchFilters?.button ?? {}),
  '&:active, &:focus': {
    backgroundColor: 'transparent',
    color: 'inherit'
  }
}))

const StyledInputLabel = styledComponent(InputLabel)(({ theme }: { theme: Theme }) => ({
  ...(theme?.custom?.SearchFilters?.inputLabel ?? {})
}))

const SearchMapModal = dynamic(() => import('../../../profiles/SearchMapModal'), {
  ssr: false
})

export type SearchFiltersProps = {
  languages: Language[]
  insurers: Insurer[]
  specialties: ProfileKeywordRelated[]
  specialtyName?: string
  postAction?: () => void
}

export type Filter = {
  onChangeFilter?: (x: any) => void
  name: string
  value: string | number
  showingCondition: boolean
  label: string
  resetOption: { value: string | number; label: string }
  options: { value: string | number; label: string }[]
}

function SearchFilters({ insurers, languages, specialties }: SearchFiltersProps) {
  const translate = useTranslateMessage()
  const [activeFilterName, setActiveFilterName] = useState<string | null>(null)
  const { changeFilter } = useContext(SearchProfilesPageContext)

  const theme = useTheme()

  const { showMapModal, toggleMapModal, filtersToDisplay } = useSearchFiltersConfig({
    insurers,
    languages,
    specialties
  })

  const activeFilter = useMemo(
    () => filtersToDisplay.find((filter) => filter.name === activeFilterName),
    [activeFilterName, filtersToDisplay]
  )

  return (
    <>
      <Hidden lgDown>
        <Grid item pt={{ xs: 0, lg: 0 }}>
          <Typography variant="h4" fontWeight="600">
            {translate('search.filters')}
          </Typography>
        </Grid>
      </Hidden>
      {filtersToDisplay.map((filter, index) => {
        switch (filter.type) {
          case FilterType.checkbox: {
            const { Icon, label, value, onChangeFilter, testId } = filter as CheckboxFilterType
            return (
              <Grid data-testid={testId} item pt="20px" key={label + index}>
                <FormControlLabel
                  sx={{
                    marginLeft: 0
                  }}
                  control={
                    <StyledCheckbox
                      checked={value}
                      onChange={onChangeFilter}
                      sx={{
                        '&.MuiCheckbox-root': {
                          padding: '0 4px 0 0',
                          color: value ? '#406AE3' : '#8794AE'
                        }
                      }}
                    />
                  }
                  label={
                    <Grid container wrap="nowrap" alignItems="center">
                      {Icon && (
                        <Icon
                          sx={{
                            width: '24px',
                            height: '24px',
                            paddingRight: '4px'
                          }}
                        />
                      )}
                      <Typography variant="body2">{label}</Typography>
                    </Grid>
                  }
                />
              </Grid>
            )
          }
          case FilterType.dropdown: {
            const { label, value, resetOption, name, options, onChangeFilter, testId } =
              filter as unknown as DropdownFilterType
            return (
              <Grid item pt={2} key={label + index}>
                <Hidden lgDown>
                  <StyledInputLabel>
                    <Typography mb={0.5} variant="body2">
                      {label}:
                    </Typography>
                  </StyledInputLabel>
                  <StyledSelect
                    data-testid={testId}
                    fullWidth
                    size="small"
                    value={value}
                    variant={theme.custom?.SearchFilters?.chipsVariant}
                    // @ts-ignore
                    onChange={(ev) => {
                      if (onChangeFilter) {
                        onChangeFilter(ev.target.value as string)
                      } else {
                        changeFilter(name, ev.target.value as string)
                      }
                    }}
                    displayEmpty
                  >
                    {resetOption && (
                      <StyledMenuItem value={resetOption.value}>
                        <Typography variant="body2" textTransform="capitalize" overflow="hidden" fontSize={15}>
                          {resetOption.label}
                        </Typography>
                      </StyledMenuItem>
                    )}
                    {options?.map((option) => (
                      <StyledMenuItem key={option.label + option.value} value={option.value}>
                        <Typography variant="body2" textTransform="capitalize" overflow="hidden" fontSize={15}>
                          {option.label}
                        </Typography>
                      </StyledMenuItem>
                    ))}
                  </StyledSelect>
                </Hidden>
                <Hidden lgUp>
                  <Typography variant="body1">{label}:</Typography>
                  <Grid
                    onClick={() => setActiveFilterName(name)}
                    alignItems="center"
                    justifyContent="space-between"
                    container
                    pt={1}
                  >
                    <StyledSelect
                      data-testid={testId}
                      fullWidth
                      size="small"
                      value={value}
                      variant={theme.custom?.SearchFilters?.chipsVariant}
                      endAdornment={<ArrowRightAlt />}
                      IconComponent={() => null}
                      displayEmpty
                      readOnly
                    >
                      {resetOption && <StyledMenuItem value={resetOption.value}>{resetOption.label}</StyledMenuItem>}
                      {options?.map((option) => (
                        <StyledMenuItem key={option.label + option.value} value={option.value}>
                          <Typography variant="body2" textTransform="capitalize" overflow="hidden" fontSize={15}>
                            {option.label}
                          </Typography>
                        </StyledMenuItem>
                      ))}
                    </StyledSelect>
                  </Grid>
                </Hidden>
              </Grid>
            )
          }
          case FilterType.button: {
            const { onClick, label, testId } = filter as ButtonFilterType
            return (
              <Grid item pt={'20px'} key={label + index}>
                <StyledFilterButton data-testid={testId} variant="outlined" fullWidth onClick={onClick}>
                  <LocationOnIcon
                    sx={{
                      width: '16px',
                      height: '16px'
                    }}
                    color="primary"
                  />
                  <Typography ml={1} fontWeight={500} variant="body1">
                    {label}
                  </Typography>
                </StyledFilterButton>
              </Grid>
            )
          }
          default: {
            return null
          }
        }
      })}
      <Hidden lgUp>
        {activeFilter && (
          <MobileFilterDetailsModal
            isOpen={Boolean(activeFilter)}
            onClose={() => setActiveFilterName(null)}
            // @ts-ignore
            filter={activeFilter}
            changeFilter={changeFilter}
          />
        )}
      </Hidden>
      <SearchMapModal isOpen={showMapModal} onClose={toggleMapModal} />
    </>
  )
}

export default SearchFilters
